import {
  AfterViewInit,
  Component, ElementRef, Injector,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { DisclosureProcessingService } from 'src/app/services/disclosure-processing.service';
import { MenuService } from 'src/app/services/menu.service';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { blockMenuClicksWhileDisclosuresProcess } from 'src/app/shared/utils/disclosure-processing-utils';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { TpoFeaturePermissions } from '../tpo-app-details/tpo-app-details.component';
import { UrlaValidationService } from 'src/app/services/urla-validation.service';
import { Constants } from 'src/app/services/constants';
import { MenuItemStatus } from 'src/app/modules/tpo/models/enums/menu-item-status.enum';
import { ApplicationContext } from 'src/app/models';
import { TransactionType } from 'src/app/models/config/loan-purpose.model';
import { LoanService } from 'src/app/services/loan';
import { KeyDateType } from 'src/app/models/key-dates/key-date-types.enum';
import { LoanServiceType } from 'src/app/modules/loan-services/service-readiness/models';
import { ServiceReadinessService } from 'src/app/modules/loan-services/service-readiness/services/service-readiness.service';
import { ServiceReadinessResult } from 'src/app/modules/loan-services/service-readiness/models/service-readiness.model';

@Component({
  selector: 'tpo-menu',
  templateUrl: './tpo-menu.component.html',
  styleUrls: ['./tpo-menu.component.scss'],
  standalone: false
})
export class TpoMenuComponent extends ApplicationContextBoundComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChildren(MenuItemComponent)
  menuItems: QueryList<MenuItemComponent> | undefined;

  @Input()
  loanId: number;

  @Input()
  featurePermissions: TpoFeaturePermissions;

  @Input()
  disclosuresEnabled: boolean = true;

  tab: string;

  isReadOnly: boolean = false;

  disabledTpoAppMenuItems: readonly string[] = [];

  protected uniqueId: string;

  protected isManualPricingEntryAvailable: boolean = false;

  protected isCompanyPRMG: boolean = false;
  protected isCompanyPulseProcessing: boolean = false;
  protected isCompanySmartfi: boolean = false;
  protected isReverseMortgage: boolean = false;

  protected showUWDecision: boolean = false;

  protected tpoSubmitted: boolean = false;

  // Which collapsible menu is currently expanded (accordion - only one at a time)
  protected expandedMenuId: string | null = null;

  protected readonly MenuItemStatus = MenuItemStatus;
  protected urlaStatus: MenuItemStatus = MenuItemStatus.Pending;

  protected disclosuresPending: boolean = false;

  private _contextSubscription: Subscription;
  private _menuItemStatusSubscription: Subscription;
  private _routerEventSubscription: Subscription;
  private _readinessSubscription: Subscription;
  private _disclosureProcessingSubscription: Subscription;
  private _disclosureProcessingClickBlocker: (() => void) | null = null;

  serviceReadinessMap = new Map<LoanServiceType, ServiceReadinessResult>();
  protected readonly LoanServiceType = LoanServiceType;

  constructor(
    private readonly injector: Injector,
    private readonly _menuService: MenuService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _loanService: LoanService,
    private readonly _urlaValidationService: UrlaValidationService,
    private readonly _serviceReadinessService: ServiceReadinessService,
    private readonly _disclosureProcessingService: DisclosureProcessingService,
    private readonly _elementRef: ElementRef<HTMLElement>,
  ) {
    super(injector);

    this._disclosureProcessingSubscription = this.applicationContextService.context
      .pipe(
        map(context => context?.isCompanyPRMG ? context.application?.applicationId : null),
        distinctUntilChanged(),
        switchMap(applicationId => applicationId
          ? this._disclosureProcessingService.isProcessing$(applicationId)
          : of(false))
      )
      .subscribe(isProcessing => {
        this.disclosuresPending = isProcessing;
      });

    this.uniqueId = uuidv4();
    this._routerEventSubscription = this._router.events.subscribe(
      (event) => {
        if (event instanceof NavigationEnd) {
          this._getInitTab();
        }
      }
    );

    this._contextSubscription = this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
      this.initialize(context);
      if (context.application?.applicationId) {
        this.loadServiceReadiness(context.application.applicationId);
      }
    });
  }

  ngOnInit(): void {
    this._activatedRoute.params.subscribe(params => {
      this._getInitTab();
    });

    this.initialize(this.applicationContext);
    if (this.applicationContext.application?.applicationId) {
      this.loadServiceReadiness(this.applicationContext.application.applicationId);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this._disclosureProcessingClickBlocker = blockMenuClicksWhileDisclosuresProcess(
      this._elementRef.nativeElement,
      () => this.disclosuresPending
    );

    this.isReverseMortgage = this.applicationContext.globalConfig.loanPurpose.find(x =>
      x.loanPurposeId == this.applicationContext?.application?.loanPurposeId
    )?.transactionType == TransactionType.ReverseMortgage;

    this.applicationContext.application.mortgageLoan.isReverseMortgage = this.isReverseMortgage;

    const urlaFieldsConfig = await this._urlaValidationService.getUrlaFieldsConfigForMortgage(this.applicationContext.application.mortgageLoan,
      !!this.applicationContext.application.losIdentifier, true);

    this._menuService.setStatus('borrowerInfo', this._urlaValidationService.getStatusForBorrowersInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan, true));

    this._menuService.setStatus('reo', this._urlaValidationService.getStatusForReo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('loanAndProperty', this._urlaValidationService.getStatusForLoanProperty(urlaFieldsConfig, this.applicationContext.application.mortgageLoan, this.featurePermissions.isAppraisedValueHidden));
    this._menuService.setStatus('declarations', this._urlaValidationService.getStatusForDeclarations(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('militaryService', this._urlaValidationService.getStatusForMilitaryService(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('demographics', this._urlaValidationService.getStatusForDemographics(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('loanOriginatorInfo', this._urlaValidationService.getStatusForLoanOriginatorInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan, this.featurePermissions.isMersEnabled, this.featurePermissions.isRequiredLoanAndCaseNumbers));
    this._menuService.setStatus('propertyLoanInfo', this._urlaValidationService.getStatusForPropertyLoanInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('titleInfo', this._urlaValidationService.getStatusForTitleInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('mortgageLoanInfo', this._urlaValidationService.getStatusForMortgageLoanInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('qualifyingTheBorr', this._urlaValidationService.getStatusForQualifyingTheBorr(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('homeownershipEducation', this._urlaValidationService.getStatusForHomeownershipEducation(urlaFieldsConfig, this.applicationContext.application.mortgageLoan));
    this._menuService.setStatus('reverseMortgageLoanInfo', this._urlaValidationService.getStatusForReverseMortgageLoanInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan, this.applicationContext.application));

    this._menuItemStatusSubscription = this._menuService.menuItemStatusChanged.subscribe((e) => {
      if (e.id === 'urla') {
        this.urlaStatus = e.status as MenuItemStatus;
      }
      const menuItem = this.menuItems?.find((mi) => mi.id === e.id);
      if (menuItem) {
        menuItem.status = e.status;
      }
    });

    this.applicationContextService.getMortgageCalculationDetails(this.applicationContext.application.mortgageLoan.mortgageId).subscribe((calcDetails) => {
      const fundsToClose = calcDetails.fundsToClose;
      this._menuService.setStatus('financialInfo', this._urlaValidationService.getStatusForFinancialInfo(urlaFieldsConfig, this.applicationContext.application.mortgageLoan, fundsToClose));
      this.urlaStatus = this._urlaValidationService.getStatusForUrla(
        urlaFieldsConfig,
        this.applicationContext.application,
        fundsToClose,
        this.featurePermissions.isMersEnabled,
        this.featurePermissions.isRequiredLoanAndCaseNumbers,
        true
      );
      this._menuService.setStatus('urla', this.urlaStatus);
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._menuItemStatusSubscription?.unsubscribe();
    this._contextSubscription?.unsubscribe();
    this._routerEventSubscription?.unsubscribe();
    this._readinessSubscription?.unsubscribe();
    this._disclosureProcessingSubscription?.unsubscribe();
    this._disclosureProcessingClickBlocker?.();
  }

  isServiceNotReady(serviceType: LoanServiceType): boolean {
    const result = this.serviceReadinessMap.get(serviceType);
    return result ? !result.isReady : false;
  }

  getServiceReadinessTooltip(serviceType: LoanServiceType): string {
    const result = this.serviceReadinessMap.get(serviceType);
    return result?.ineligibilityMessage ?? '';
  }

  private loadServiceReadiness(applicationId: number): void {
    this._readinessSubscription?.unsubscribe();
    this._readinessSubscription = this._serviceReadinessService
      .evaluateForApplication(applicationId)
      .subscribe({
        next: (response) => {
          this.serviceReadinessMap.clear();
          for (const result of response.results) {
            this.serviceReadinessMap.set(result.loanServiceType, result);
          }
        },
      });
  }

  onListItemClicked = (e: any, tabToActivate: string) => {
    if (this.tab === tabToActivate) {
      e.preventDefault();
      return;
    }
    this.tab = tabToActivate;
  }

  // Accordion toggle: collapse if already expanded, otherwise expand (collapsing any other)
  protected toggleMenuExpansion(menuId: string): void {
    this.expandedMenuId = this.expandedMenuId === menuId ? null : menuId;
  }

  // Opening a section from its row link always expands it; navigation only auto-expands when the route changes.
  protected expandMenu(menuId: string): void {
    this.expandedMenuId = menuId;
  }

  // Simple expansion state getters - single source of truth
  protected get isServicesExpanded(): boolean {
    return this.expandedMenuId === 'services';
  }

  protected get isContactsExpanded(): boolean {
    return this.expandedMenuId === 'contacts';
  }

  protected get isUrlaExpanded(): boolean {
    return this.expandedMenuId === 'urla';
  }

  protected get isPricingExpanded(): boolean {
    return this.expandedMenuId === 'pricing';
  }

  onMenuItemClicked = (id: string) => {
    this._menuService.onMenuItemClicked(id);
  }

  // The section holding the current page. Row highlighting follows this; the chevron shows what is expanded.
  protected get activeMenuId(): string | null {
    return this._getMenuIdForRoute(this.tab);
  }

  // Map routes to their parent menu for auto-expansion
  private _getMenuIdForRoute(tab: string): string | null {
    const menuRoutes: Record<string, string[]> = {
      services: ['credit-reports', 'digital-emp-income-verification', 'digital-asset-verification', 'aus', 'disclosures', 'appraisal', 'link', 'link-income-calculator', 'income-analysis'],
      contacts: ['change-of-circumstance', 'escalation-history', 'loan-docs-v3', 'internal-contacts', 'external-contacts', 'key-dates', 'loan-activity', 'esignature-history'],
      pricing: ['pricing', 'pricing-history', 'application-pricing-scenarios', 'amortization'],
      urla: ['urla']
    };

    for (const [menuId, routes] of Object.entries(menuRoutes)) {
      if (routes.includes(tab)) return menuId;
    }
    return null;
  }

  private initialize = (context: ApplicationContext) => {
    if (!context.application) {
      return;
    }

    this.disabledTpoAppMenuItems = context.userPermissions.disabledTPOAppMenuItems;
    this.getRecentUW(context.application.applicationId);
    this.isCompanyPRMG = context.isCompanyPRMG;
    this.isCompanyPulseProcessing = context.isCompanyPulseProcessing;
    this.isCompanySmartfi = context.isCompanySmartfi;
    if (context.isTpo) {
      const keyDates = context.applicationKeyDatesByType;
      if (keyDates?.[KeyDateType.LEIssued]?.eventDate || keyDates?.[KeyDateType.InitialDisclosureSent]?.eventDate) {
        this.isReadOnly = true;
      }

      const menuStatuses = context.tpoMenuStatuses;
      if (this.isCompanyPulseProcessing) {
        let feesItemStatus = menuStatuses[Constants.menu.servicesMenuItems.fees];
        if (feesItemStatus) {
          feesItemStatus = MenuItemStatus.Success;
        }
      }
      const submissionMenuItemStatus = menuStatuses[Constants.menu.servicesMenuItems.submission];
      this.tpoSubmitted = submissionMenuItemStatus && submissionMenuItemStatus.toLowerCase() == MenuItemStatus.Success;
      if (this.tpoSubmitted) {
        this.isReadOnly = true;
      }
    }
    this.isManualPricingEntryAvailable = context.isCompanyMortgageProcessing;
  }

  private _getInitTab = () => {
    const splitUrl = this._router.url.split("/");

    // init page
    const previousTab = this.tab;
    setTimeout(() => {
      if (splitUrl.length < 5) {
        this.tab = 'loan-summary';
      } else {
        const pageType = splitUrl[splitUrl.length - 1];
        this.tab = pageType ? pageType : 'loan-summary';
      }

      // Only auto-expand when navigating to a different route
      if (previousTab !== this.tab) {
        this.expandedMenuId = this._getMenuIdForRoute(this.tab);
      }
    });
  }

  private getRecentUW = (loanId: number) => {
    this._loanService.getRecentUWDoc(loanId).subscribe({
      next: (response) => {
        if (response) {
          this.showUWDecision = true;
        }
      }
    });
  }
}

export class ParentMenuItemDescriptor {
  listItemId: string;
  childListId: string;
}
